import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor, ShieldCheck, FileSignature, Check, Ban, Clock, Stamp,
  AlertTriangle, ExternalLink, Eye, EyeOff, Loader2, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_VIEW = {
  active: {
    dot: "bg-[var(--green)]",
    headline: "KATLAS node active",
    sub: "Live signing is online — actions on this page will produce a real KATLAS-signed receipt.",
    tone: "border-[var(--green)] bg-[#ECFDF5]",
  },
  unconfigured: {
    dot: "bg-[var(--amber)]",
    headline: "View-only mode",
    sub: "An active KATLAS connection is required to sign new decisions. The page is read-only until then.",
    tone: "border-[#FDE68A] bg-[#FFFBEB]",
  },
  error: {
    dot: "bg-[var(--red)]",
    headline: "KATLAS node unreachable",
    sub: "The signing node did not respond. Try again in a moment, or check diagnostics.",
    tone: "border-[#FECACA] bg-[#FEF2F2]",
  },
};

export default function LiveKatlas() {
  const [status, setStatus] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null); // "approved" | "refused"
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.katlasStatus().then(setStatus).catch(() => setStatus({ status: "error", label: "Status probe failed" }));
    api.freightScenario().then(setScenario).catch(() => {});
  }, []);

  const run = async (outcome) => {
    setBusy(outcome); setError(null); setReceipt(null);
    try {
      const fn = outcome === "approved" ? api.freightApprove : api.freightRefuse;
      const r = await fn(note.slice(0, 280));
      setReceipt(r);
      toast.success(
        outcome === "approved"
          ? "Governed response approved · KATLAS-signed receipt issued"
          : "Governed response refused · KATLAS-signed receipt issued",
        { description: r.receipt_hash }
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || { error: "request_failed", message: String(e?.message || e) });
      if (detail?.error === "katlas_unconfigured") {
        toast.error("KATLAS unconfigured — production credential not set");
      } else {
        toast.error("Could not sign receipt", { description: detail?.message || String(e?.message || e) });
      }
    } finally {
      setBusy(null);
    }
  };

  const isActive = status?.status === "active";
  const view = STATUS_VIEW[status?.status || "error"] || STATUS_VIEW.error;

  return (
    <div className="space-y-5" data-testid="live-katlas-root">
      {/* KATLAS status banner */}
      <div className={`border ${view.tone} p-4`} data-testid="katlas-status-banner">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className={`w-2.5 h-2.5 mt-1.5 rounded-full ${view.dot}`}/>
            <div>
              <div className="display text-[15px]" data-testid="katlas-status-label">
                {view.headline}
              </div>
              <div className="text-[12px] text-[var(--ink)] mt-0.5 max-w-[64ch]">{view.sub}</div>
              <div className="mono text-[10px] text-[var(--ink-soft)] mt-2" data-testid="katlas-status-technical">
                technical state: {status?.label || "—"} · simulation fallback: {String(status?.simulation_fallback_allowed ?? false)}
              </div>
            </div>
          </div>
          <Link to="/sandbox/diagnostics" className="text-[12px] text-[var(--navy)] fc-link-underline" data-testid="open-diagnostics">
            Diagnostics →
          </Link>
        </div>
      </div>

      {/* rec-02 · explicit role + action lead-in */}
      <div className="border-l-2 border-[var(--navy)] pl-4 py-1.5" data-testid="role-lead-in">
        <div className="fc-eyebrow mb-1">Acting as · Freight Exception Coordinator</div>
        <p className="text-[13px] text-[var(--ink)] leading-snug max-w-[72ch]">
          Review the evidence below, then approve or refuse the governed response.
          {isActive
            ? " Your decision will produce a KATLAS-signed receipt."
            : " Approving and refusing is disabled while in view-only mode."}
        </p>
      </div>

      {/* Scenario header */}
      {scenario && (
        <div className="fc-card p-5" data-testid="freight-scenario-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 grid place-items-center bg-[var(--navy)] text-white rounded-sm">
              <Anchor size={16}/>
            </div>
            <div>
              <div className="fc-eyebrow mb-1">Freight exception · {scenario.scenario.policy_gate}</div>
              <h1 className="display text-[22px] leading-tight">{scenario.scenario.title}</h1>
              <p className="text-[13px] text-[var(--ink-soft)] mt-2 max-w-[80ch] leading-relaxed">
                {scenario.scenario.summary}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {scenario.scenario.exceptions.map(x => (
                  <span key={x} className="fc-chip fc-chip-escalated" data-testid={`exception-${x}`}>
                    <AlertTriangle size={10}/> {x.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CAR framing */}
      {scenario && (
        <div className="grid sm:grid-cols-3 fc-bento" data-testid="car-block">
          {["custody", "authority", "receipts"].map(k => {
            const c = scenario.car[k];
            return (
              <div key={k} className="p-5">
                <div className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)] mb-2">{c.label}</div>
                <div className="text-[14px] leading-snug mb-2">{c.description}</div>
                {c.holder && <div className="mono text-[11px] text-[var(--ink-soft)]">{c.holder}</div>}
              </div>
            );
          })}
        </div>
      )}
      {scenario && (
        <div className="text-[12px] text-[var(--ink-soft)] border-l-2 border-[var(--navy)] pl-3 italic">
          {scenario.car.line}
        </div>
      )}

      {/* rec-01 · Evidence boundary — two grouped lists instead of a 6×5 matrix */}
      {scenario && (
        <div className="fc-card" data-testid="evidence-matrix">
          <div className="px-5 py-4 border-b border-[var(--line)]">
            <div className="display text-[15px]">Evidence boundary · what KATLAS sees, what stays at source</div>
            <div className="text-[12px] text-[var(--ink-soft)] mt-0.5">
              KATLAS receives references, hashes, metadata and assertions. Raw commercial data and withheld assets stay with the holder.
            </div>
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--line)]">
            <EvidenceColumn
              tone="sent"
              title="Sent to KATLAS"
              caption="Hashed references travel to the signing node. Public receipt exposes the reference."
              rows={scenario.evidence_matrix.filter(r => r.sent_to_katlas)}
            />
            <EvidenceColumn
              tone="withheld"
              title="Withheld · internal only"
              caption="Controlled by the holder. Not sent to KATLAS. Not on the public receipt."
              rows={scenario.evidence_matrix.filter(r => !r.sent_to_katlas)}
            />
          </div>
          <div className="px-5 py-3 border-t border-[var(--line)] text-[11px] text-[var(--ink-soft)]">
            {scenario.data_boundary}
          </div>
        </div>
      )}

      {/* Action block */}
      <div className="fc-card p-5" data-testid="action-block">
        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <div className="display text-[16px] mb-1">Governed action</div>
            <p className="text-[13px] text-[var(--ink-soft)] mb-3">
              Approve or refuse the governed response package. The selected action will be sent to KATLAS for live signing.
            </p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 280))}
              placeholder="Optional reviewer note (plain text · ≤280 chars)"
              data-testid="reviewer-note"
              className="rounded-sm min-h-[80px]"
              maxLength={280}
            />
            <div className="mono text-[10px] text-[var(--ink-soft)] mt-1 text-right">{note.length}/280</div>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-2 justify-center">
            <Button
              onClick={() => run("approved")}
              disabled={!isActive || busy !== null}
              className="bg-[var(--navy)] hover:bg-black rounded-sm h-11 text-[14px]"
              data-testid="approve-btn"
            >
              {busy === "approved" ? <><Loader2 size={14} className="mr-2 animate-spin"/> Signing…</>
                                   : <><Check size={14} className="mr-2"/> Approve governed response</>}
            </Button>
            <Button
              onClick={() => run("refused")}
              disabled={!isActive || busy !== null}
              variant="outline"
              className="rounded-sm h-11 text-[14px]"
              data-testid="refuse-btn"
            >
              {busy === "refused" ? <><Loader2 size={14} className="mr-2 animate-spin"/> Signing…</>
                                  : <><Ban size={14} className="mr-2"/> Refuse / hold for review</>}
            </Button>
            {!isActive && (
              <div className="text-[11px] text-[var(--ink-soft)] mt-1 text-center" data-testid="action-disabled-reason">
                Connect KATLAS to enable signing. Until then, this is a view-only demo.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error surface */}
      {error && (
        <div className="border border-[var(--red)] bg-[#FEF2F2] p-4" data-testid="action-error">
          <div className="display text-[14px] text-[var(--red)] mb-1">Could not sign receipt</div>
          <div className="text-[12px] mono">{error.error || "request_failed"}</div>
          <div className="text-[12px] text-[var(--ink)] mt-1">{error.message}</div>
        </div>
      )}

      {/* Live receipt */}
      {receipt && <LiveReceipt receipt={receipt}/>}
    </div>
  );
}

function Yes() { return <span className="inline-flex items-center gap-1 text-[var(--green)] mono text-[11px]"><Eye size={11}/> yes</span>; }
function NoBoundary() { return <span className="inline-flex items-center gap-1 text-[var(--ink-soft)] mono text-[11px]"><EyeOff size={11}/> withheld</span>; }

function EvidenceColumn({ tone, title, caption, rows }) {
  const isSent = tone === "sent";
  return (
    <div className="p-5" data-testid={`evidence-column-${tone}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${isSent ? "bg-[var(--teal)]" : "bg-[var(--ink-soft)]"}`}/>
        <div className="display text-[14px]">{title}</div>
        <span className="mono text-[10px] text-[var(--ink-soft)] ml-auto">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
      </div>
      <p className="text-[11px] text-[var(--ink-soft)] mb-3 max-w-[44ch]">{caption}</p>
      <ul className="space-y-2">
        {rows.map(r => (
          <li
            key={r.label}
            data-testid={`matrix-row-${r.label}`}
            className={`border-l-2 ${isSent ? "border-[var(--teal)]" : "border-[var(--ink-soft)]"} pl-3 py-1.5`}
          >
            <div className="text-[13px] leading-tight">{r.label.replace(/_/g, " ")}</div>
            <div className="mono text-[10px] text-[var(--ink-soft)] mt-0.5 break-all">{r.ref}</div>
            <div className="mono text-[10px] text-[var(--ink-soft)] mt-0.5">held by: {r.controlled_by}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LiveReceipt({ receipt }) {
  const meta = receipt.action_outcome === "approved"
    ? { Icon: Stamp,  chip: "fc-chip-approved", label: "Approved · KATLAS-signed" }
    : { Icon: Clock,  chip: "fc-chip-escalated", label: "Refused / on hold · KATLAS-signed" };
  const Icon = meta.Icon;
  const publicUrl = `${window.location.origin}/r/${encodeURIComponent(receipt.receipt_hash)}`;
  const fmtTs = (ts) => { try { return new Date(ts).toUTCString(); } catch { return ts; } };

  const copy = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Public receipt link copied"); }
    catch { /* ignore */ }
  };

  return (
    <div className="fc-card border-l-4 border-l-[var(--navy)] fc-fade-up" data-testid="live-receipt">
      {/* 1 · Outcome (the business result first) */}
      <div className="px-5 py-4 border-b border-[var(--line)] flex flex-wrap items-center gap-3">
        <FileSignature size={16} className="text-[var(--navy)]" strokeWidth={1.7}/>
        <div className="flex-1 min-w-0">
          <div className="fc-eyebrow">KATLAS-signed receipt</div>
          <div className="display text-[15px]">{receipt.action_taken}</div>
        </div>
        <span className={`fc-chip ${meta.chip}`} data-testid="receipt-status-chip"><Icon size={11}/> {meta.label}</span>
      </div>

      {/* 2 · Trust anchors at a glance (signer · node timestamp · verification) */}
      <div className="p-5 grid sm:grid-cols-3 gap-4 border-b border-[var(--line)]" data-testid="receipt-trust-anchors">
        <Anchor2
          label="Verification"
          value={<span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-[var(--green)]"/>
            <span className="mono">{receipt.verification_status || "—"}</span>
          </span>}
          sub={receipt.verification || "—"}
        />
        <Anchor2
          label="Signer"
          value={<span className="mono text-[12px] break-all">{receipt.signer_ref || `${receipt.signer_issuer || "—"}#${receipt.signer_key_id || "—"}`}</span>}
          sub="KATLAS node signing identity"
        />
        <Anchor2
          label="Node timestamp"
          value={<span className="mono text-[12px]">{fmtTs(receipt.node_timestamp)}</span>}
          sub="Time the KATLAS node attested the action"
        />
      </div>

      {/* 3 · CAR + data boundary (governance summary) */}
      <div className="p-5 border-b border-[var(--line)]">
        <div className="fc-eyebrow mb-2">CAR · Custody · Authority · Receipts</div>
        <div className="text-[12px] grid sm:grid-cols-2 gap-3">
          <div className="border border-[var(--line)] p-3">
            <span className="mono uppercase text-[10px] tracking-wider text-[var(--ink-soft)]">Custody holder</span>
            <div className="mono mt-1">{receipt.car?.custody_holder}</div>
          </div>
          <div className="border border-[var(--line)] p-3">
            <span className="mono uppercase text-[10px] tracking-wider text-[var(--ink-soft)]">Authority holder</span>
            <div className="mono mt-1">{receipt.car?.authority_holder}</div>
          </div>
        </div>
        <div className="text-[12px] mt-3 italic text-[var(--ink-soft)]">{receipt.car?.line || receipt.car_line}</div>
        <div className="text-[12px] mt-3 bg-[var(--paper)] border border-[var(--line)] p-3" data-testid="receipt-data-boundary">
          <strong>Data boundary:</strong> {receipt.data_boundary}
        </div>
      </div>

      {/* 4 · Shared evidence (what KATLAS saw) */}
      <div className="p-5 border-b border-[var(--line)]">
        <div className="fc-eyebrow mb-2">Shared evidence sent to KATLAS</div>
        <div className="border border-[var(--line)] divide-y divide-[var(--line)]">
          {(receipt.shared_evidence_refs || []).map((e, i) => (
            <div key={i} className="px-3 py-2 text-[12px] flex justify-between gap-3" data-testid={`shared-ref-${i}`}>
              <span className="mono">{e.label}</span>
              <span className="mono text-[var(--ink-soft)] break-all">{e.ref}</span>
            </div>
          ))}
        </div>
        <div className="text-[12px] mt-3">
          <span className="mono text-[var(--ink-soft)]">Withheld evidence (not sent to KATLAS):</span>{" "}
          <span className="mono">{receipt.withheld_evidence_count}</span>
        </div>
      </div>

      {/* 5 · Technical details — collapsed by default */}
      <details className="px-5 py-3 border-b border-[var(--line)] group" data-testid="receipt-tech-details">
        <summary className="cursor-pointer fc-eyebrow flex items-center gap-2 list-none">
          <span className="text-[var(--ink-soft)] group-open:rotate-90 transition-transform inline-block">▸</span>
          Technical details
        </summary>
        <div className="mt-3 grid lg:grid-cols-2 gap-x-6">
          <div>
            <Kv label="Receipt hash"   value={<span className="mono break-all" data-testid="receipt-hash">{receipt.receipt_hash}</span>}/>
            <Kv label="Attestation ref" value={<span className="mono break-all">{receipt.attestation_ref}</span>}/>
            {receipt.signer_fingerprint && <Kv label="Signer fingerprint" value={<span className="mono break-all">{receipt.signer_fingerprint}</span>}/>}
          </div>
          <div>
            <Kv label="Acting role"      value={receipt.acting_role}/>
            <Kv label="Policy gate"      value={<span className="mono">{receipt.policy_gate}</span>}/>
            <Kv label="Local timestamp"  value={<span className="mono">{fmtTs(receipt.local_timestamp)}</span>}/>
            <Kv label="Outcome"          value={<span className="mono">{receipt.action_outcome}</span>}/>
          </div>
        </div>
      </details>

      {/* 6 · Public URL — always visible (this is what gets shared) */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-3">
        <span className="mono text-[11px] text-[var(--ink-soft)]">Public-safe receipt URL:</span>
        <span className="mono text-[11px] text-[var(--navy)] truncate">{publicUrl}</span>
        <Button size="sm" variant="outline" onClick={copy} className="rounded-sm h-7 text-[11px] ml-auto" data-testid="copy-public-url">
          <Copy size={11} className="mr-1.5"/> Copy
        </Button>
        <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="open-public-url"
           className="text-[11px] text-[var(--navy)] inline-flex items-center gap-1 fc-link-underline">
          Open <ExternalLink size={10}/>
        </a>
      </div>
    </div>
  );
}

function Anchor2({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="fc-eyebrow mb-1">{label}</div>
      <div className="text-[14px] min-w-0">{value}</div>
      {sub && <div className="text-[11px] text-[var(--ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-[12px] border-b border-[var(--line)] last:border-0">
      <div className="fc-eyebrow pt-0.5">{label}</div>
      <div className="leading-snug min-w-0">{value || <span className="text-[var(--ink-soft)]">—</span>}</div>
    </div>
  );
}
